import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
} from "@mui/material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

import api from "../api/axios";

// Change this to the actual location of your rig PNG
import rigImage from "../assets/drilling-rig.png";

export default function RigStatusWidget() {
  const [rigs, setRigs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  const touchStartX = useRef(null);

  /* ========================================================= */
  /* FETCH RIG STATUS */
  /* ========================================================= */

  useEffect(() => {
    const fetchRigStatus = async () => {
      try {
        setLoading(true);

        const { data: response } = await api.get("/dashboard/rig-status");

        const rigData = Array.isArray(response?.rigs) ? response.rigs : [];

        setRigs(rigData);
        setCurrentIndex(0);
      } catch (error) {
        console.error("Failed to fetch rig status:", error);
        setRigs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRigStatus();
  }, []);

  /* ========================================================= */
  /* AUTO SCROLL */
  /* ========================================================= */

  useEffect(() => {
    if (rigs.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % rigs.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [rigs.length, isPaused]);

  /* ========================================================= */
  /* NAVIGATION */
  /* ========================================================= */

  const showPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? rigs.length - 1 : prev - 1));
  };

  const showNext = () => {
    setCurrentIndex((prev) => (prev + 1) % rigs.length);
  };

  /* ========================================================= */
  /* TOUCH / SWIPE */
  /* ========================================================= */

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;

    const touchEndX = event.changedTouches[0].clientX;
    const difference = touchStartX.current - touchEndX;

    // Minimum swipe distance
    if (Math.abs(difference) > 50) {
      if (difference > 0) {
        showNext();
      } else {
        showPrevious();
      }
    }

    touchStartX.current = null;
  };

  /* ========================================================= */
  /* LOADING */
  /* ========================================================= */

  if (loading) {
    return (
      <Box
        sx={{
          mt: 3,
          width: "100%",
          minHeight: 190,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.paper",
          borderRadius: 2,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  /* ========================================================= */
  /* NO RIGS */
  /* ========================================================= */

  if (rigs.length === 0) {
    return (
      <Box
        sx={{
          mt: 3,
          width: "100%",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          p: 1,
          backdropFilter: "blur(12px)",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Typography color="text.secondary">No rig status available</Typography>
      </Box>
    );
  }

  const rig = rigs[currentIndex];
  const stats = rig.stats || {};
  const lastSeen = rig.last_seen || {};

  return (
    <Box
      sx={{
        mt: 3,
        width: "100%",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        p: 1,
        backdropFilter: "blur(12px)",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ===================================================== */}
      {/* RIG CONTENT */}
      {/* ===================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: 190,
          px: { xs: 1, sm: 2 },
          py: 1.5,
        }}
      >
        {/* =================================================== */}
        {/* LEFT NAVIGATION */}
        {/* =================================================== */}

        {rigs.length > 1 && (
          <IconButton
            onClick={showPrevious}
            size="small"
            sx={{
              flexShrink: 0,
              mr: { xs: 0.5, sm: 1 },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        {/* =================================================== */}
        {/* RIG IMAGE */}
        {/* =================================================== */}

        <Box
          sx={{
            width: { xs: 90, sm: 120, md: 140 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={rigImage}
            alt={rig.rig_name}
            sx={{
              width: { xs: 75, sm: 100, md: 120 },
              height: { xs: 75, sm: 100, md: 120 },
              objectFit: "cover",
              borderRadius: "50%",
              border: "2px solid",
              borderColor: "divider",
              display: "block",
            }}
          />

          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              mt: 0.7,
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            {rig.rig_name}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color:
                rig.ownership === "Third Party"
                  ? "warning.main"
                  : "success.main",
              textAlign: "center",
              mt: 0.2,
            }}
          >
            {rig.ownership}
          </Typography>
        </Box>

        {/* =================================================== */}
        {/* RIG INFORMATION */}
        {/* =================================================== */}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            ml: { xs: 1, sm: 2 },
          }}
        >
          {/* ========================================================= */}
          {/* LAST SEEN */}
          {/* ========================================================= */}

          <Box sx={{ mb: 1.2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                fontSize: "0.68rem",
                lineHeight: 1.2,
                mb: 0.2,
              }}
            >
              Last Seen
            </Typography>

            {/* DAYS AGO - PRIMARY */}
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{
                lineHeight: 1.3,
                color:
                  lastSeen.days_ago === 0
                    ? "success.main"
                    : lastSeen.days_ago >= 7
                      ? "warning.main"
                      : "text.primary",
              }}
            >
              {lastSeen.days_ago === 0
                ? "Today"
                : lastSeen.days_ago === 1
                  ? "1 day ago"
                  : `${lastSeen.days_ago} days ago`}
            </Typography>

            {/* BH + PROJECT - SECONDARY */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.25,
                fontSize: "0.7rem",
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={`${lastSeen.bh_code || "-"} - ${lastSeen.project || "-"}`}
            >
              {lastSeen.bh_code || "-"} - {lastSeen.project || "-"}
            </Typography>
          </Box>

          {/* ================================================= */}
          {/* STATISTICS */}
          {/* ================================================= */}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              columnGap: { xs: 1, sm: 2 },
              rowGap: 0.8,
            }}
          >
            <StatItem
              label="Total Meterage"
              value={`${Number(stats.total_meterage || 0).toFixed(2)} m`}
            />

            <StatItem label="Working Days" value={stats.working_days ?? 0} />

            <StatItem label="Idle Days" value={stats.idle_days ?? 0} />

            <StatItem
              label="Best m/day"
              value={`${Number(stats.best_m_per_day || 0).toFixed(2)}`}
            />

            <StatItem label="BH Completed" value={stats.completed_bhs ?? 0} />

            <StatItem
              label="Max. Depth (m)"
              value={Number(stats.max_depth || 0).toFixed(2)}
            />
          </Box>
        </Box>

        {/* =================================================== */}
        {/* RIGHT NAVIGATION */}
        {/* =================================================== */}

        {rigs.length > 1 && (
          <IconButton
            onClick={showNext}
            size="small"
            sx={{
              flexShrink: 0,
              ml: { xs: 0.5, sm: 1 },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {/* ===================================================== */}
      {/* PAGE INDICATOR */}
      {/* ===================================================== */}

      {rigs.length > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            pb: 1,
          }}
        >
          {/* PAGE INDICATORS */}
          <Stack direction="row" spacing={0.6} alignItems="center">
            {rigs.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: index === currentIndex ? 18 : 6,
                  height: 6,
                  borderRadius: 3,
                  bgcolor:
                    index === currentIndex ? "primary.main" : "text.disabled",
                  transition: "all 0.25s ease",
                }}
              />
            ))}
          </Stack>

          {/* PLAY / PAUSE */}
          <IconButton
            size="small"
            onClick={() => setIsPaused((prev) => !prev)}
            title={
              isPaused
                ? "Resume automatic scrolling"
                : "Pause automatic scrolling"
            }
            sx={{
              width: 28,
              height: 28,
              ml: 0.5,
            }}
          >
            {isPaused ? (
              <PlayArrowIcon sx={{ fontSize: 18 }} />
            ) : (
              <PauseIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

/* ========================================================= */
/* STAT ITEM */
/* ========================================================= */

function StatItem({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{
          fontSize: "0.68rem",
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          mt: 0.15,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
